import type { ReactNode } from 'react';
import {
  NewsIcon, CartIcon, ChartIcon, TruckIcon, WalletIcon, CheckCircleIcon,
} from '../client/clientIcons.js';
import {
  BuildingIcon, ReceiptIcon, WarehouseIcon, TagIcon, UsersIcon,
  PackageIcon, GroupsIcon, FactoryIcon, LegalIcon, StoreIcon,
  UserSingleIcon, CarIcon, ServiceIcon, ListIcon, AiIcon,
} from './internalIcons.js';
import type { SectionId } from '../../navigation/sections.js';

/**
 * Иконки разделов — отдельным модулем, потому что нужны в двух местах:
 * в сайдбаре (`InternalShell`) и на плитках «Рабочего стола» (`Home`).
 *
 * Общие с клиентским кабинетом иконки берём из `clientIcons`, специфичные —
 * из `internalIcons`. Дублировать SVG не нужно.
 */

/** Акцентный цвет плитки раздела — по мокапу разделы различаются оттенком. */
export type SectionTone = { fg: string; bg: string };

const TONES: Record<SectionId, SectionTone> = {
  news: { fg: '#0E9F6E', bg: '#E7F6EF' },
  orders: { fg: '#2563EB', bg: '#E8EFFE' },
  counterparties: { fg: '#7C3AED', bg: '#F1EBFE' },
  ar: { fg: '#0E9F6E', bg: '#E7F6EF' },
  stock: { fg: '#F08C00', bg: '#FEF3E2' },
  'commercial-terms': { fg: '#7C3AED', bg: '#F1EBFE' },
  sales: { fg: '#2563EB', bg: '#E8EFFE' },
  shipped: { fg: '#0891B2', bg: '#E3F5F9' },
  'plan-fact': { fg: '#E8590C', bg: '#FDEEE3' },
  pnl: { fg: '#0E9F6E', bg: '#E7F6EF' },
  ai: { fg: '#7C3AED', bg: '#F1EBFE' },
  card41: { fg: '#B45309', bg: '#FDF3E3' },
  reconcile: { fg: '#0891B2', bg: '#E3F5F9' },
  clients: { fg: '#0E9F6E', bg: '#E7F6EF' },
  'pending-counterparties': { fg: '#F08C00', bg: '#FEF3E2' },
};

export function sectionTone(id: SectionId): SectionTone {
  return TONES[id];
}

export function sectionIcon(id: SectionId, size = 20): ReactNode {
  switch (id) {
    case 'news': return <NewsIcon size={size} />;
    case 'orders': return <CartIcon size={size} />;
    case 'counterparties': return <BuildingIcon size={size} />;
    case 'ar': return <ReceiptIcon size={size} />;
    case 'stock': return <WarehouseIcon size={size} />;
    case 'commercial-terms': return <TagIcon size={size} />;
    case 'sales': return <ChartIcon size={size} />;
    case 'shipped': return <TruckIcon size={size} />;
    case 'plan-fact': return <ChartIcon size={size} />;
    case 'pnl': return <WalletIcon size={size} />;
    case 'ai': return <AiIcon size={size} />;
    case 'card41': return <WalletIcon size={size} />;
    case 'reconcile': return <CheckCircleIcon size={size} />;
    case 'clients': return <UsersIcon size={size} />;
    case 'pending-counterparties': return <BuildingIcon size={size} />;
  }
}

export function adminSectionIcon(id: string, size = 20): ReactNode {
  switch (id) {
    case 'products': return <PackageIcon size={size} />;
    case 'product-groups': return <GroupsIcon size={size} />;
    case 'producers': return <FactoryIcon size={size} />;
    case 'legal-entities': return <LegalIcon size={size} />;
    case 'suppliers': return <StoreIcon size={size} />;
    case 'users': return <UserSingleIcon size={size} />;
    case 'carrier': return <TruckIcon size={size} />;
    case 'vehicle': return <CarIcon size={size} />;
    case 'driver': return <UserSingleIcon size={size} />;
    case 'extra_service': return <ServiceIcon size={size} />;
    case 'order_type': return <ListIcon size={size} />;
    case 'warehouse': return <WarehouseIcon size={size} />;
    default: return <PackageIcon size={size} />;
  }
}
